"use client";

import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@devhop/ui";
import { CheckIcon, ChevronsUpDown, XIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import type { CascadingCategoryItem } from "./CascadingCategorySelect";

const MAIN_ALL_LABEL = "ໝວດໝູ່ຫຼັກ — ທັງໝົດ";
const SUB_ALL_LABEL = "ໝວດໝູ່ຍ່ອຍ — ທັງໝົດ";

type CategoryOption = {
  id: string;
  name: string;
  /** Number of sellable products in this category (incl. its sub-categories). */
  productCount: number;
};

type ComboboxProps = {
  options: CategoryOption[];
  /** Selected category id, or "" for the "all" option. */
  value: string;
  onChange: (categoryId: string) => void;
  allLabel: string;
  disabled?: boolean;
};

function CategoryCombobox({
  options,
  value,
  onChange,
  allLabel,
  disabled,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Width of the trigger box captured on open, so the dropdown matches it exactly.
  const [triggerWidth, setTriggerWidth] = useState<number>();

  const selectedName = useMemo(
    () => options.find((o) => o.id === value)?.name ?? "",
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search]);

  const commit = (id: string) => {
    onChange(id);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setTriggerWidth(triggerRef.current?.offsetWidth);
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <div className="relative w-full">
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="h-8 w-full justify-between pe-14 font-normal"
          >
            <span className="flex-1 truncate text-left">
              {selectedName || allLabel}
            </span>
            <ChevronsUpDown className="ms-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {value && !disabled ? (
          <button
            type="button"
            aria-label="ລຶບການເລືອກ"
            className="absolute end-7 top-1/2 -translate-y-1/2 rounded-sm bg-background p-0.5 text-muted-foreground hover:text-foreground"
            onClick={() => onChange("")}
          >
            <XIcon className="size-4" />
          </button>
        ) : null}
      </div>
      <PopoverContent
        className="w-[min(92vw,18rem)] p-0"
        align="start"
        style={triggerWidth ? { width: triggerWidth } : undefined}
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="ຄົ້ນຫາໝວດ..."
            className="h-9"
          />
          <CommandList className="max-h-72 overflow-auto">
            <CommandEmpty>ບໍ່ພົບໝວດທີ່ຄົ້ນຫາ</CommandEmpty>
            <CommandItem
              value="__all__"
              onSelect={() => commit("")}
              className="items-start"
            >
              <span className="wrap-break-word flex-1 whitespace-normal text-muted-foreground">
                {allLabel}
              </span>
              <CheckIcon
                className={cn(
                  "ms-2 mt-0.5 size-4 shrink-0",
                  value ? "opacity-0" : "opacity-100",
                )}
              />
            </CommandItem>
            {filtered.map((opt) => (
              <CommandItem
                key={opt.id}
                value={opt.id}
                onSelect={() => commit(opt.id)}
                className="items-start"
              >
                <span
                  className="wrap-break-word flex-1 whitespace-normal"
                  title={opt.name}
                >
                  {opt.name}
                </span>
                <span className="ms-2 mt-0.5 flex shrink-0 items-center gap-2">
                  {opt.productCount > 0 ? (
                    <span
                      className="rounded-full bg-primary px-1.5 py-0.5 text-primary-foreground text-xs tabular-nums"
                      title={`${opt.productCount} ສິນຄ້າ`}
                    >
                      {opt.productCount}
                    </span>
                  ) : null}
                  <CheckIcon
                    className={cn(
                      "size-4",
                      value === opt.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type Props = {
  categories: CascadingCategoryItem[];
  /** Currently applied categoryId filter (may be a root, sub, or deeper node). */
  value: string;
  onChange: (categoryId: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  /** Sellable product count per category id (direct — rolled up across the subtree here). */
  productCountById?: Map<string, number>;
};

/** Root-first path of ids from the top-level ancestor down to `id`. */
function pathFromValue(
  byId: Map<string, CascadingCategoryItem>,
  id: string,
): string[] {
  const path: string[] = [];
  let cur: string | null = id;
  const guard = new Set<string>();
  while (cur && !guard.has(cur)) {
    const node = byId.get(cur);
    if (!node) break;
    guard.add(cur);
    path.unshift(cur);
    cur = node.parentId ?? null;
  }
  return path;
}

export function MainSubCategorySelect({
  categories,
  value,
  onChange,
  isLoading,
  disabled,
  productCountById,
}: Props) {
  const byId = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );

  // Roll up the direct per-category product counts across each category's subtree,
  // so a main category shows the total products beneath it (matches subtree filtering).
  const rolledCount = useMemo(() => {
    const childrenByParent = new Map<string, string[]>();
    for (const c of categories) {
      const parent = c.parentId ?? null;
      if (parent) {
        const arr = childrenByParent.get(parent) ?? [];
        arr.push(c.id);
        childrenByParent.set(parent, arr);
      }
    }
    const memo = new Map<string, number>();
    const rollup = (id: string, stack: Set<string>): number => {
      const cached = memo.get(id);
      if (cached !== undefined) return cached;
      if (stack.has(id)) return 0; // guard against cyclic data
      stack.add(id);
      let total = productCountById?.get(id) ?? 0;
      for (const childId of childrenByParent.get(id) ?? []) {
        total += rollup(childId, stack);
      }
      stack.delete(id);
      memo.set(id, total);
      return total;
    };
    const result = new Map<string, number>();
    for (const c of categories) result.set(c.id, rollup(c.id, new Set()));
    return result;
  }, [categories, productCountById]);

  // Options (sorted, with product counts) for the direct children of `parentId`.
  const optionsOf = useMemo(() => {
    const byParent = new Map<string | null, CategoryOption[]>();
    for (const c of categories) {
      const parent = c.parentId ?? null;
      const arr = byParent.get(parent) ?? [];
      arr.push({
        id: c.id,
        name: c.name,
        productCount: rolledCount.get(c.id) ?? 0,
      });
      byParent.set(parent, arr);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => a.name.localeCompare(b.name, "lo"));
    }
    return byParent;
  }, [categories, rolledCount]);

  // Always show two fixed boxes — Main (level 0) and the first Sub (level 1) — even
  // before a main is picked. Deeper levels (2nd, 3rd, …) are added automatically for
  // each selected category that actually has sub-categories.
  const levels = useMemo(() => {
    const path = value && byId.has(value) ? pathFromValue(byId, value) : [];
    const mainId = path[0] ?? "";
    const out: Array<{ parentId: string | null; selectedId: string }> = [
      // Level 0: main — options are the root categories.
      { parentId: null, selectedId: mainId },
      // Level 1: first sub — always shown; empty/disabled until a main with children.
      { parentId: mainId, selectedId: path[1] ?? "" },
    ];
    // Levels 2+: auto — one box per already-selected sub that has deeper children.
    for (let i = 1; i < path.length; i++) {
      const id = path[i];
      if (id && (optionsOf.get(id)?.length ?? 0) > 0) {
        out.push({ parentId: id, selectedId: path[i + 1] ?? "" });
      }
    }
    return out;
  }, [value, byId, optionsOf]);

  const controlsDisabled = disabled || isLoading;

  return (
    <>
      {levels.map((level, idx) => {
        const levelOptions = optionsOf.get(level.parentId) ?? [];
        return (
          <CategoryCombobox
            key={idx === 0 ? "main" : level.parentId || "sub"}
            options={levelOptions}
            value={level.selectedId}
            // Selecting a category filters by it; clearing falls back to the parent level.
            onChange={(v) => onChange(v || (level.parentId ?? ""))}
            allLabel={idx === 0 ? MAIN_ALL_LABEL : SUB_ALL_LABEL}
            // The fixed first sub box is disabled when there are no options to pick.
            disabled={controlsDisabled || levelOptions.length === 0}
          />
        );
      })}
    </>
  );
}
